import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import {
	DiscountTypeEnum,
	EventDto,
	EventTicketDto,
	FileVariantsEnum,
	formatEventTimeRange,
	getAttendeeUserName,
} from '@sweatpals/shared';
import { Box, Theme, Typography, useMediaQuery } from '@mui/material';
import { isBefore } from 'date-fns';
import {
	Colors,
	CoreIconButton,
	LinkIcon,
	Showable,
	CalendarEmptyIcon,
	LocationIcon,
	TicketIcon,
	DropDownIcon,
	DropDownUpIcon,
	CoreButtonBase,
	SettingsIcon,
	DownloadIcon,
	EventDetailsIcon,
	UserAvatar,
	ArrowRightIcon,
	PromoCodeIcon,
} from '../../';
import { CoverImage } from '../cover-image';
import { CoreQrCode } from '../core-qr-code';
import {
	backgroundStyles,
	cardStyles,
	iconButtonStyles,
	eventInfoItemStyles,
	eventNameStyles,
	qrCodeLabelStyles,
	qrCodeWrapperStyles,
	footerButtonStyles,
	hostStyles,
} from './ticket-card.styles';
import { TicketMenu, TicketMenuProps } from './ticket-menu.component';

export interface TicketCardProps {
	ticket: EventTicketDto;
	event: EventDto;
	currentUserId?: string | null;
	logoComponent?: React.ComponentType<any>;
	appleWalletImage?: string;
	defaultBackgroundImage?: string;
	isOrderOwner?: boolean;
	isPastTicket?: boolean;
	eventPageUrl?: string;
	hostPageUrl?: string;

	// Translation functions
	t?: (key: string) => string;

	// Utility functions
	getMediaUrl(fileId: string, variant?: FileVariantsEnum): string;
	copyToClipboard(text: string): void;

	// Callback functions
	onCopyEventLink?(): void;
	onOpenTicketDetailsModal?(): void;
	onOpenCheckoutPage?(): void;
	onOpenTransferTicketPage?(): void;
	onOpenUpgradeTicketPage?(): void;
	onDownloadTicket?(): void;
	onAddToCalendar?(): void;

	// Menu props
	ticketMenuProps?: Partial<TicketMenuProps>;
}

export const TicketCard: FC<TicketCardProps> = ({
	ticket,
	currentUserId,
	event,
	logoComponent: LogoComponent,
	appleWalletImage,
	defaultBackgroundImage,
	isOrderOwner: providedIsOrderOwner,
	isPastTicket: providedIsPastTicket,
	eventPageUrl,
	hostPageUrl,
	t,
	getMediaUrl,
	copyToClipboard,
	onCopyEventLink,
	onOpenTicketDetailsModal,
	onOpenCheckoutPage,
	onOpenTransferTicketPage,
	onOpenUpgradeTicketPage,
	onDownloadTicket,
	onAddToCalendar,
	ticketMenuProps = {},
}) => {
	const [isTicketMenuOpen, setIsTicketMenuOpen] = useState(false);
	const ref = useRef<HTMLButtonElement | null>(null);

	const isOrderOwner = useMemo(() => {
		if (providedIsOrderOwner !== undefined) return providedIsOrderOwner;
		return ticket.order?.userId === currentUserId;
	}, [providedIsOrderOwner, ticket.order?.userId, currentUserId]);

	const isPastTicket = useMemo(() => {
		if (providedIsPastTicket !== undefined) return providedIsPastTicket;
		if (!ticket.order?.eventInstanceDate) return false;
		return isBefore(new Date(ticket.order.eventInstanceDate), new Date());
	}, [providedIsPastTicket, ticket.order?.eventInstanceDate]);

	const copyEventLink = useCallback(async (): Promise<void> => {
		if (!eventPageUrl) return;
		copyToClipboard(eventPageUrl);
		onCopyEventLink?.();
	}, [copyToClipboard, eventPageUrl, onCopyEventLink]);

	const handleCloseTicketMenu = useCallback(() => {
		setIsTicketMenuOpen(false);
	}, []);

	const handleOpenTicketMenu = useCallback(() => {
		setIsTicketMenuOpen(true);
	}, []);

	const handleOpenTicketDetailsModal = useCallback((): void => {
		if (!ticket.order) return;
		handleCloseTicketMenu();
		onOpenTicketDetailsModal?.();
	}, [ticket.order, handleCloseTicketMenu, onOpenTicketDetailsModal]);

	const showTicketMenu = useMemo(() => !!(
			onOpenCheckoutPage ||
			onOpenTransferTicketPage ||
			onOpenUpgradeTicketPage ||
			onOpenTicketDetailsModal
	), [onOpenCheckoutPage, onOpenTransferTicketPage, onOpenUpgradeTicketPage, onOpenTicketDetailsModal]);

	const handleOpenCheckoutPage = useCallback((): void => {
		onOpenCheckoutPage?.();
	}, [onOpenCheckoutPage]);

	const handleOpenTransferTicketPage = useCallback((): void => {
		onOpenTransferTicketPage?.();
	}, [onOpenTransferTicketPage]);

	const handleOpenUpgradeTicketPage = useCallback((): void => {
		onOpenUpgradeTicketPage?.();
	}, [onOpenUpgradeTicketPage]);

	const handleDownloadTicket = useCallback((): void => {
		onDownloadTicket?.();
	}, [onDownloadTicket]);

	const handleAddToCalendar = useCallback((): void => {
		onAddToCalendar?.();
	}, [onAddToCalendar]);

	const mdMedia = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

	return (
		<Box data-component="ticket" sx={cardStyles}>
			<Box sx={backgroundStyles}>
				<CoverImage
					alt="event-avatar"
					src={event?.avatarId ? getMediaUrl(event.avatarId) : defaultBackgroundImage}
					sx={{ position: 'absolute', top: 0, left: 0 }}
				/>
				<Showable show={!!event?.avatarId}>
					<Box sx={{ backdropFilter: 'blur(164px)', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} />
				</Showable>
			</Box>
			<Box position="relative" display="flex" flexDirection="column" rowGap="30px">
				<Box display="flex" flexDirection="column" rowGap="20px">
					<Box display="flex" flexDirection="column" rowGap="10px">
						<Box display="flex" justifyContent="space-between" alignItems="center">
							{LogoComponent && (
								<Box
									component={LogoComponent}
									sx={{ width: '80px', height: '22px', minWidth: '80px', minHeight: '22px' }}
								/>
							)}
							<CoreIconButton sx={iconButtonStyles} onClick={copyEventLink}>
								<LinkIcon width={18} height={18} />
							</CoreIconButton>
						</Box>
						<Box display="flex" columnGap="24px">
							<Box flex={1} display="flex" flexDirection="column" overflow="hidden" rowGap="10px">
								<Box component="a" href={eventPageUrl} target="_blank" sx={eventNameStyles}>
									{event?.name}
								</Box>
								{event?.author && (
									<Box component="a" href={hostPageUrl} target="_blank" sx={hostStyles}>
										<UserAvatar
											fullName={event.author.fullName}
											avatarSrc={event.author.avatarId && getMediaUrl(event.author.avatarId)}
											emptyAvatarVariant={event.author.emptyAvatarVariant}
											sx={{ width: '24px', height: '24px' }}
										/>
										<Typography>{event.author.fullName ?? event.author.userName}</Typography>
										<ArrowRightIcon />
									</Box>
								)}
							</Box>
							{event?.avatarId && (
								<CoverImage
									src={getMediaUrl(event.avatarId)}
									sx={{
										width: '67px',
										height: '86px',
										borderRadius: '8px',
										boxShadow: `0px 12px 18px -10px ${Colors.black_a40}`,
									}}
								/>
							)}
						</Box>
					</Box>
					<Box display="flex" flexDirection="column" rowGap="12px">
						<Showable show={!event?.additionalData?.hideEventDate}>
							<Box sx={eventInfoItemStyles}>
								<CalendarEmptyIcon width={20} height={20} />
								<Typography>
									{event
										? formatEventTimeRange(event.startDate, null, {
												timezone: event.tzid,
												startDateFormat: 'eee, LLL dd, yyyy p',
												showTimezone: false,
											})
										: ''}
								</Typography>
							</Box>
						</Showable>
						<Box sx={eventInfoItemStyles}>
							<LocationIcon />
							<Typography>{event?.isOnlineEvent ? 'Online event' : event?.addressName}</Typography>
						</Box>
						<Showable show={!!ticket?.eventPrice}>
							<Box sx={eventInfoItemStyles}>
								<TicketIcon />
								<Typography>{ticket?.eventPrice?.tierName}</Typography>
							</Box>
						</Showable>
						<Showable show={ticket?.discount}>
							<Box sx={eventInfoItemStyles}>
								<PromoCodeIcon />
								<Typography>
									{ticket.discount?.code?.toUpperCase() ?? ''} •{' '}
									{ticket.discount?.type === DiscountTypeEnum.Percentage
										? `${ticket.discount?.amount ?? 0}%`
										: `${((ticket.discount?.amount ?? 0) / 100).toFixed(2)}$`}
								</Typography>
							</Box>
						</Showable>
					</Box>
					<Showable hide={isPastTicket}>
						<Box display="flex" justifyContent="center">
							<Box sx={qrCodeWrapperStyles}>
								<CoreQrCode
									link={ticket.id}
									size={130}
									qrCodeOptions={{
										qrOptions: {
											errorCorrectionLevel: 'Q',
											typeNumber: 6,
										},
									}}
									qrCodeSx={{ borderRadius: '12px', p: '12px' }}
								/>
								<Typography sx={qrCodeLabelStyles}>{getAttendeeUserName(ticket, ticket.order)}</Typography>
							</Box>
						</Box>
					</Showable>
				</Box>
				<Box display="flex" columnGap="8px" flexWrap="wrap">
					<Showable show={!!onDownloadTicket && !mdMedia}>
						<Box sx={{ flex: 1 }}>
							<CoreButtonBase
								ref={ref}
								variant="secondary"
								sx={{
									...footerButtonStyles,
									backgroundColor: Colors.white,
									border: 'none',
									color: Colors.black,
									width: '100%',
									marginBottom: '10px',
								}}
								onClick={handleDownloadTicket}
							>
								<img src={appleWalletImage} style={{ width: '22px', height: '16px' }} alt="apple-wallet" />
								{t ? t('tickets.ticket-card.add-to-apple-wallet') : 'Add to Apple Wallet'}
							</CoreButtonBase>
						</Box>
					</Showable>
					<Showable show={!!onAddToCalendar}>
						<CoreButtonBase
							ref={ref}
							variant="secondary"
							sx={footerButtonStyles}
							onClick={handleAddToCalendar}
						>
							<CalendarEmptyIcon width={20} height={20} />
							{t ? t('tickets.ticket-card.add-to-calendar') : 'Add to Calendar'}
						</CoreButtonBase>
					</Showable>
					<Showable show={!!onDownloadTicket}>
						<CoreButtonBase
							ref={ref}
							variant="secondary"
							sx={footerButtonStyles}
							onClick={handleDownloadTicket}
						>
							<DownloadIcon />
							{t ? t('tickets.ticket-card.download-ticket') : 'Download'}
						</CoreButtonBase>
					</Showable>
					<Showable show={isPastTicket && isOrderOwner}>
						<CoreButtonBase
							ref={ref}
							variant="secondary"
							sx={footerButtonStyles}
							onClick={handleOpenTicketDetailsModal}
						>
							<EventDetailsIcon />
							{t ? t('tickets.ticket-card.order-details') : 'Order Details'}
						</CoreButtonBase>
					</Showable>
					<Showable hide={isPastTicket || !showTicketMenu}>
						<CoreButtonBase ref={ref} variant="secondary" sx={footerButtonStyles} onClick={handleOpenTicketMenu}>
							<SettingsIcon width={24} height={24} />
							{t ? t('tickets.ticket-card.manage-ticket-button') : 'Manage Ticket'}
							{isTicketMenuOpen ? <DropDownUpIcon /> : <DropDownIcon />}
						</CoreButtonBase>
					</Showable>
				</Box>
			</Box>
			<Showable show={showTicketMenu}>
				<TicketMenu
					open={isTicketMenuOpen}
					anchorEl={ref.current}
					isOrderOwner={isOrderOwner}
					onClose={handleCloseTicketMenu}
					onOrderDetailsButtonClick={handleOpenTicketDetailsModal}
					onAddMoreTicketsButtonClick={handleOpenCheckoutPage}
					onTransferTicketButtonClick={handleOpenTransferTicketPage}
					onUpgradeTicketButtonClick={handleOpenUpgradeTicketPage}
					t={t}
					{...ticketMenuProps}
				/>
			</Showable>
		</Box>
	);
};
